import { useCallback, useState } from 'react'

import clsx from 'clsx'
import ImageAddLineIcon from 'remixicon-react/ImageAddLineIcon'

import { Image, Space } from '@tribeplatform/gql-client/types'
import {
  useNetwork,
  usePage,
  useUpdateSpace,
  useUploadFile,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { linkToFile } from '../../../../lib/upload'
import { PopoverImagePicker } from '../../../Picker/ImagePicker'
import { GoogleSEOCard } from '../../../SeoCards/Google'
import { TwitterSEOCard } from '../../../SeoCards/Twitter'

const ImagePicker = ({ thumbnailUrl, onUpload, isLoading }) => {
  return (
    <div
      className="text-center mx-auto relative max-w-full border flex h-48 bg-cover"
      style={{ backgroundImage: thumbnailUrl ? `url(${thumbnailUrl})` : '' }}
    >
      <div className="absolute top-0 bottom-0 left-0 right-0 cursor-pointer flex justify-center items-center">
        <PopoverImagePicker
          tabsProps={{
            file: {
              helper: (
                <>
                  <p>Recommended size is 1200 x 630 pixels.</p>
                  <p>The file size limit is 10MB.</p>
                </>
              ),
            },
          }}
          onRemove={() => {
            onUpload(undefined)
          }}
          onLinkChange={async link => {
            const file = await linkToFile(link)
            if (file) onUpload(file)
          }}
          onImageChange={onUpload}
        >
          <div
            className={clsx(
              thumbnailUrl && 'bg-black opacity-70 rounded-md px-2 py-1',
            )}
          >
            {isLoading ? (
              <SpinnerIcon className="w-9 h-9 animate-spin" />
            ) : (
              <>
                <div className="mb-3 text-basicSurface-300 flex justify-center">
                  <ImageAddLineIcon className="w-9 h-9" />
                </div>
                <Link>Choose an image</Link>
                <div className="text-basicSurface-300">
                  PNG, JPG, GIF up to 10MB
                </div>
              </>
            )}
          </div>
        </PopoverImagePicker>
      </div>
    </div>
  )
}

const TitleHelperText = ({ value }) => (
  <div className="flex justify-between text-basicSurface-300">
    <span>Recommended length: 60 characters</span>
    <span
      className={clsx(
        'text-bold',
        // eslint-disable-next-line no-nested-ternary
        value
          ? value.length > 60
            ? 'text-warning-800'
            : 'text-success-800'
          : '',
      )}
    >
      Length: {value?.length || 0}
    </span>
  </div>
)

const DescriptionHelperText = ({ value }) => (
  <div className="flex justify-between text-basicSurface-300">
    <span>Recommended length: 155 - 160 characters</span>
    <span
      className={clsx(
        'text-bold',
        // eslint-disable-next-line no-nested-ternary
        value
          ? value.length > 160 || value.length < 155
            ? 'text-warning-800'
            : 'text-success-800'
          : '',
      )}
    >
      Length: {value?.length || 0}
    </span>
  </div>
)

export const SpaceSeoSettings = ({ space }: { space: Space }) => {
  const { data: network } = useNetwork()
  const { loadingFile, uploadFile } = useUploadFile()
  const { mutateAsync: updateSpace, isLoading } = useUpdateSpace()
  const { data: page } = usePage({
    variables: {
      path: space.address?.path,
    },
    useQueryOptions: {
      cacheTime: 0,
      enabled: !!space.address?.path,
    },
  })
  const [customFields, setCustomFields] = useState<{
    title?: string
    description?: string
    noIndex?: boolean
    thumbnail?: Image
  }>({
    title: space.customSeoDetail.title,
    description: space.customSeoDetail.description,
    noIndex: space.customSeoDetail.noIndex,
    thumbnail: space.customSeoDetail.thumbnail as Image,
  })

  const setField = useCallback(
    (key, value) => {
      setCustomFields({
        ...customFields,
        [key]: value,
      })
    },
    [customFields, setCustomFields],
  )

  const onUploadFile = useCallback(
    async (file: File | undefined) => {
      if (!file) {
        setField('thumbnail', undefined)
      } else {
        uploadFile(file, async uploadedImage => {
          if (uploadedImage.id) {
            setField('thumbnail', uploadedImage)
          }
        })
      }
    },
    [uploadFile, setField],
  )

  const onSubmit = async () => {
    try {
      await updateSpace({
        id: space.id,
        input: {
          customSeoDetail: {
            title: customFields.title,
            description: customFields.description,
            noIndex: customFields.noIndex,
            thumbnailId: customFields.thumbnail?.id,
          },
        },
      })
      toast({
        title: 'Space updated',
        status: 'success',
      })
    } catch (e) {
      toast({
        title: 'Unable to update space',
        status: 'error',
      })
    }
  }

  const thumbnailUrl = loadingFile?.url || customFields.thumbnail?.url
  const previewProps = {
    title: customFields.title || page?.seoDetail?.title,
    description: customFields.description || page?.seoDetail?.description,
    thumbnailUrl:
      customFields.thumbnail?.url || (page?.seoDetail?.thumbnail as Image)?.url,
    domain: network.domain,
  }

  return (
    <div className="col-span-12 md:col-span-8 flex flex-col space-y-5">
      <div>
        <h2 className="text-basicMain-900">SEO</h2>
        <p className="mt-3 text-sm text-basicSurface-500">
          Search engine optimization allows you to improve your ranking in
          search results and make it easier for people to find your page when
          they search for it.
        </p>
      </div>
      <div>
        <h6 className="text-basicSurface-500 mt-3 mb-1">
          GOOGLE RESULT PREVIEW
        </h6>
        <GoogleSEOCard {...previewProps} />
        <h6 className="text-basicSurface-500 mt-3 mb-1">
          TWITTER RESULT PREVIEW
        </h6>
        <TwitterSEOCard {...previewProps} />
      </div>
      <div className="space-y-5">
        <FormControl.Input
          value={customFields.title}
          onChange={e => {
            setField('title', e.target.value)
          }}
          label="SEO Title"
          name="title"
          placeholder="If empty, we will autogenerate this based on post title"
          helperText={<TitleHelperText value={customFields.title} />}
        />
        <FormControl.Textarea
          value={customFields.description}
          onChange={e => {
            setField('description', e.target.value)
          }}
          name="description"
          placeholder="If empty, we will autogenerate this based on post title"
          label="SEO Description"
          helperText={
            <DescriptionHelperText value={customFields.description} />
          }
        />
        <div>
          <FormControl.Label>Banner</FormControl.Label>
          <div className="mt-1">
            <ImagePicker
              onUpload={onUploadFile}
              isLoading={!!loadingFile}
              thumbnailUrl={thumbnailUrl}
            />
          </div>
          <FormControl.HelperText className="mt-2">
            You can choose a custom banner for your space to be represented on
            your SEO card
          </FormControl.HelperText>
        </div>
        <div className="flex flex-col space-y-3">
          <p className="text-sm text-basicSurface-500">
            Search results typically show your SEO title and description. Your
            title is also the browser window title. Depending on the search
            engine, descriptions displayed can be 50 to 300 characters long. If
            you don’t add a title or description, search engines will use your
            page title and content.
          </p>
          <FormControl.Toggle
            label="Hide from search results"
            name="noIndex"
            checked={customFields.noIndex}
            onChange={() => {
              setField('noIndex', !customFields.noIndex)
            }}
          />
        </div>
        <div className="flex justify-end">
          <Button type="submit" loading={isLoading} onClick={onSubmit}>
            Update
          </Button>
        </div>
      </div>
    </div>
  )
}
